/** Dashboard — list competitions, create new ones. */

import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/api/client";
import { useAuthStore } from "@/stores/auth";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import type { Competition, ScoringModelInfo } from "@/types";

const ROLE_COLORS: Record<string, string> = {
  admin: "#3b82f6",
  judge: "#22c55e",
  producer: "#a855f7",
};

export function DashboardPage() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const [competitions, setCompetitions] = useState<Competition[]>([]);
  const [showCreate, setShowCreate] = useState(false);
  const [scoringModels, setScoringModels] = useState<Record<string, ScoringModelInfo>>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get<Competition[]>("/api/competitions"),
      api.get<Record<string, ScoringModelInfo>>("/api/scoring-models"),
    ]).then(([c, m]) => {
      setCompetitions(c);
      setScoringModels(m);
      setLoading(false);
    });
  }, []);

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="loading-spinner" />
      </div>
    );
  }

  const myCompetitions = competitions.filter((c) => c.owner_id === user?.id);
  const invitedCompetitions = competitions.filter((c) => c.owner_id !== user?.id);

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Competitions</h1>
          <p className="page-subtitle">Competitions and events you manage or judge</p>
        </div>
        <Button onClick={() => setShowCreate(true)}>New Competition</Button>
      </div>

      {competitions.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-title">No competitions yet</div>
          <div className="empty-state-desc">
            Create your first competition to start scoring.
          </div>
          <Button className="mt-4" onClick={() => setShowCreate(true)}>
            Create Competition
          </Button>
        </div>
      ) : (
        <>
          {/* My Competitions */}
          {myCompetitions.length > 0 && (
            <div>
              <h2 className="text-sm text-muted" style={{ textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 12 }}>
                My Competitions
              </h2>
              <div className="grid-3">
                {myCompetitions.map((c) => (
                  <Link key={c.id} to={`/competition/${c.id}`} className="card" style={{ textDecoration: "none", color: "inherit" }}>
                    <div className="card-title">{c.name}</div>
                    {c.description && (
                      <p className="text-sm text-muted" style={{ marginTop: 8 }}>{c.description}</p>
                    )}
                    <div className="flex gap-3" style={{ marginTop: 12 }}>
                      <span className="badge badge-role">
                        {scoringModels[c.scoring_model]?.name ?? c.scoring_model}
                      </span>
                      <span className="badge badge-off">{c.judge_count} judges</span>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {/* Invited Competitions */}
          {invitedCompetitions.length > 0 && (
            <div style={{ marginTop: myCompetitions.length > 0 ? 32 : 0 }}>
              <h2 className="text-sm text-muted" style={{ textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 12 }}>
                Invited Competitions
              </h2>
              <div className="grid-3">
                {invitedCompetitions.map((c) => (
                  <Link key={c.id} to={`/competition/${c.id}`} className="card" style={{ textDecoration: "none", color: "inherit" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                      <div className="card-title">{c.name}</div>
                    </div>
                    <p className="text-sm text-muted" style={{ marginTop: 4 }}>
                      {c.owner_name ? `${c.owner_name}'s competition` : "Invited competition"}
                    </p>
                    {c.description && (
                      <p className="text-sm text-muted" style={{ marginTop: 4 }}>{c.description}</p>
                    )}
                    <div className="flex gap-3" style={{ marginTop: 12, flexWrap: "wrap" }}>
                      {(c.my_roles ?? []).map((role) => (
                        <span
                          key={role}
                          className="badge"
                          style={{
                            background: ROLE_COLORS[role] ?? "var(--c-surface-2)",
                            color: "#fff",
                            textTransform: "capitalize",
                          }}
                        >
                          {role}
                        </span>
                      ))}
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {showCreate && (
        <CreateCompetitionModal
          scoringModels={scoringModels}
          onClose={() => setShowCreate(false)}
          onCreate={(c) => {
            setCompetitions([c, ...competitions]);
            setShowCreate(false);
            navigate(`/competition/${c.id}`);
          }}
        />
      )}
    </div>
  );
}

function CreateCompetitionModal({
  scoringModels,
  onClose,
  onCreate,
}: {
  scoringModels: Record<string, ScoringModelInfo>;
  onClose: () => void;
  onCreate: (c: Competition) => void;
}) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [judgeCount, setJudgeCount] = useState(3);
  const [scoringModel, setScoringModel] = useState("max");
  const [expandedModel, setExpandedModel] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const handleSubmit = async () => {
    if (!name.trim()) return;
    setSaving(true);
    try {
      const competition = await api.post<Competition>("/api/competitions", {
        name: name.trim(),
        description: description.trim() || null,
        judge_count: judgeCount,
        scoring_model: scoringModel,
      });
      onCreate(competition);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-title">New Competition</div>

        <div className="flex-col gap-4">
          <Input label="Competition Name" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Summer Nationals 2026" autoFocus />
          <Input label="Description (optional)" value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Brief description..." />

          <div className="field">
            <label className="field-label">Judge Count</label>
            <div style={{ display: "flex", gap: 4 }}>
              {Array.from({ length: 10 }, (_, i) => i + 1).map((n) => (
                <button
                  key={n}
                  className={`btn btn-sm ${judgeCount === n ? "btn-primary" : "btn-secondary"}`}
                  style={{ flex: "1 1 0", minWidth: 0, padding: "6px 0" }}
                  onClick={() => setJudgeCount(n)}
                >
                  {n}
                </button>
              ))}
            </div>
          </div>

          <div className="field">
            <label className="field-label">
              Scoring Model
              <span className="info-trigger" title="How final scores are calculated from judge scores">?</span>
            </label>
            {Object.entries(scoringModels).filter(([key]) => key === "max").map(([key, info]) => (
              <div
                key={key}
                className="card"
                style={{
                  padding: "12px",
                  marginTop: "4px",
                  cursor: "pointer",
                  borderColor: scoringModel === key ? "var(--c-accent)" : undefined,
                }}
                onClick={() => setScoringModel(key)}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <strong>{info.name}</strong>
                    <span className="text-sm text-muted" style={{ marginLeft: 8 }}>
                      {info.short}
                    </span>
                  </div>
                  <button
                    className="btn btn-ghost btn-sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      setExpandedModel(expandedModel === key ? null : key);
                    }}
                  >
                    {expandedModel === key ? "Less" : "More"}
                  </button>
                </div>
                {expandedModel === key && (
                  <div className="text-sm text-muted" style={{ marginTop: 8, lineHeight: 1.6 }}>
                    <p>{info.detail}</p>
                    <p style={{ marginTop: 6, fontStyle: "italic" }}>
                      Best for: {info.when_to_use}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="modal-actions">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} loading={saving} disabled={!name.trim()}>
            Create Competition
          </Button>
        </div>
      </div>
    </div>
  );
}
